import React, { useState } from "react";
import {
  FiChevronRight,
  FiChevronDown,
  FiMapPin,
  FiPhone,
  FiPackage,
} from "react-icons/fi";
import CustomerContactActions from "../Customer/CustomerContactActions";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const getInitials = (name) => {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name || "?")
    .slice(0, 2)
    .toUpperCase();
};

const getUrgency = (due, maxDue) => {
  if (maxDue <= 0) return "low";
  const ratio = Number(due) / maxDue;
  if (ratio >= 0.6) return "high";
  if (ratio >= 0.3) return "medium";
  return "low";
};

const urgencyStyles = {
  high: {
    ring: "ring-red-100",
    badge: "bg-red-50 text-red-700 border-red-200",
    accent: "from-red-500 to-red-600",
    due: "text-red-700",
    dueBg: "bg-red-50 border-red-100",
  },
  medium: {
    ring: "ring-amber-100",
    badge: "bg-amber-50 text-amber-800 border-amber-200",
    accent: "from-amber-500 to-amber-600",
    due: "text-amber-800",
    dueBg: "bg-amber-50 border-amber-100",
  },
  low: {
    ring: "ring-line",
    badge: "bg-subtle text-inkSecondary border-line",
    accent: "from-[#39454F] to-[#2d363f]",
    due: "text-ink",
    dueBg: "bg-subtle border-line",
  },
};

const urgencyLabels = {
  high: "High priority",
  medium: "Follow up",
  low: "Outstanding",
};

const formatQty = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const PayableCompanyCard = ({ company, maxDue, rank, onOpen }) => {
  const [itemsOpen, setItemsOpen] = useState(false);
  const due = Number(company.payable || 0);
  const urgency = getUrgency(due, maxDue);
  const style = urgencyStyles[urgency];
  const boxQty = Number(company.boxQty || 0);
  const totalPurchaseQty = Number(company.totalPurchaseQty || 0);
  const purchaseItems = Array.isArray(company.purchaseItems)
    ? company.purchaseItems.filter((i) => i.name !== "Total")
    : [];

  const hasContacts = Boolean(company.contact || company.contact2);
  const hasInventory = boxQty > 0 || totalPurchaseQty > 0;

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Open ledger for ${company.name || "company"}, payable ${formatRs(due)}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group relative w-full text-left rounded-2xl max850:rounded-xl bg-card border border-line shadow-soft hover:shadow-card hover:border-ink/25 active:scale-[0.995] transition-all duration-200 cursor-pointer overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 touch-manipulation"
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 max850:w-0.5 bg-gradient-to-b ${style.accent}`}
        aria-hidden
      />

      <div className="p-4 pl-5 max850:p-3 max850:pl-3.5">
        {/* Mobile: payable amount full-width strip */}
        <div
          className={`hidden max850:flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 mb-3 ${style.dueBg}`}
        >
          <p className="text-[11px] uppercase tracking-wider text-muted font-semibold">
            Amount payable
          </p>
          <p
            className={`shrink-0 text-xl font-extrabold tabular-nums leading-none ${style.due}`}
          >
            {formatRs(due)}
          </p>
        </div>

        {/* Header: identity + payable (desktop) */}
        <div className="flex items-start gap-3 max850:gap-2.5">
          <div
            className={`shrink-0 w-11 h-11 max850:w-10 max850:h-10 rounded-xl bg-gradient-to-br ${style.accent} text-white font-bold text-sm max850:text-xs flex items-center justify-center shadow-soft ring-4 max850:ring-2 ${style.ring}`}
            aria-hidden
          >
            {getInitials(company.name)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 max850:gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap mb-1 max850:mb-0.5">
                  {rank <= 3 && (
                    <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-ink text-white shadow-soft max850:px-1.5 max850:text-[9px]">
                      Top #{rank}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border max850:px-1.5 max850:text-[9px] ${style.badge}`}
                  >
                    {urgencyLabels[urgency]}
                  </span>
                </div>

                <h3 className="font-bold text-ink text-base leading-snug max850:text-[15px] max850:leading-tight line-clamp-2">
                  {company.name}
                </h3>

                {(company.address || hasContacts) && (
                  <div className="mt-2 max850:mt-1.5 space-y-1.5">
                    {company.address && (
                      <p className="flex items-start gap-1.5 text-xs text-muted leading-relaxed max850:text-[11px]">
                        <FiMapPin
                          size={12}
                          className="shrink-0 mt-0.5 text-muted"
                          aria-hidden
                        />
                        <span className="line-clamp-2">{company.address}</span>
                      </p>
                    )}

                    {hasContacts && (
                      <div
                        className={`grid gap-1.5 ${
                          company.contact && company.contact2
                            ? "grid-cols-1 sm:grid-cols-2 max850:grid-cols-2"
                            : "grid-cols-1"
                        }`}
                      >
                        {company.contact && (
                          <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-inkSecondary bg-subtle border border-line rounded-lg px-2.5 py-2">
                            <FiPhone
                              size={12}
                              className="shrink-0 text-muted"
                              aria-hidden
                            />
                            <span className="truncate">{company.contact}</span>
                          </span>
                        )}
                        {company.contact2 && (
                          <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-muted bg-subtle/80 border border-line rounded-lg px-2.5 py-2">
                            <FiPhone
                              size={12}
                              className="shrink-0 text-muted"
                              aria-hidden
                            />
                            <span className="truncate">{company.contact2}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop payable pill */}
              <div
                className={`shrink-0 rounded-xl border px-3 py-2 text-right max850:hidden ${style.dueBg}`}
              >
                <p className="text-[10px] uppercase tracking-wider text-muted font-semibold leading-none">
                  Payable
                </p>
                <p
                  className={`mt-1 text-lg font-extrabold tabular-nums leading-tight ${style.due}`}
                >
                  {formatRs(due)}
                </p>
              </div>
            </div>
          </div>

          <FiChevronRight
            className="shrink-0 text-muted group-hover:text-ink group-hover:translate-x-0.5 transition-all mt-2 max850:hidden"
            size={18}
            aria-hidden
          />
        </div>

        {/* Inventory chips */}
        {hasInventory && (
          <div className="mt-3 max850:mt-2.5 grid grid-cols-1 max850:grid-cols-2 gap-2">
            {boxQty > 0 && (
              <span className="inline-flex items-center justify-center max850:justify-start gap-1.5 text-[11px] font-semibold px-2.5 py-2 max850:py-2 rounded-lg bg-sky-50 text-sky-800 border border-sky-100">
                <FiPackage size={12} aria-hidden />
                <span className="truncate">Cartons: {formatQty(boxQty)}</span>
              </span>
            )}
            {totalPurchaseQty > 0 && (
              <span className="inline-flex items-center justify-center max850:justify-start text-[11px] font-semibold px-2.5 py-2 max850:py-2 rounded-lg bg-subtle text-inkSecondary border border-line">
                <span className="truncate">
                  Qty: {formatQty(totalPurchaseQty)}
                </span>
              </span>
            )}
          </div>
        )}

        {/* Items accordion */}
        {purchaseItems.length > 0 && (
          <div className="mt-3 max850:mt-2.5">
            <button
              type="button"
              aria-expanded={itemsOpen}
              aria-controls={`items-${company._id || company.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setItemsOpen((open) => !open);
              }}
              className="w-full min-h-[44px] inline-flex items-center justify-between gap-2 rounded-xl border border-line bg-card px-3 py-2.5 max850:px-2.5 text-left text-xs font-semibold text-inkSecondary hover:bg-subtle hover:border-ink/20 active:bg-subtle transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40 touch-manipulation"
            >
              <span className="text-ink min-w-0">
                <span className="max850:hidden">
                  {itemsOpen
                    ? "Hide purchased items"
                    : `Show purchased items (${purchaseItems.length})`}
                </span>
                <span className="hidden max850:inline">
                  {itemsOpen ? "Hide items" : `Items (${purchaseItems.length})`}
                </span>
              </span>
              <FiChevronDown
                size={16}
                className={`shrink-0 text-muted transition-transform duration-200 ${
                  itemsOpen ? "rotate-180" : ""
                }`}
                aria-hidden
              />
            </button>

            {itemsOpen && (
              <ul
                id={`items-${company._id || company.name}`}
                className="mt-2 rounded-xl border border-line bg-subtle/50 divide-y divide-line max-h-44 max850:max-h-52 overflow-y-auto overscroll-contain"
              >
                {purchaseItems.map((item) => (
                  <li
                    key={`${item.name}-${item.unit}`}
                    className="flex items-start justify-between gap-3 px-3 py-2.5 max850:py-3 text-[11px] max850:text-xs"
                  >
                    <span className="min-w-0 flex-1 text-inkSecondary font-medium leading-snug break-words">
                      {item.name}
                    </span>
                    <span className="shrink-0 tabular-nums font-bold text-ink text-right">
                      {formatQty(item.qty)}{" "}
                      <span className="font-semibold text-muted">
                        {item.unit}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Footer actions */}
        <div
          className="mt-3 max850:mt-2.5 pt-3 max850:pt-2.5 border-t border-line flex flex-col max850:flex-col gap-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <p className="text-[11px] text-muted font-medium group-hover:text-ink/70 transition-colors max850:hidden">
            Tap card to open ledger
          </p>

          <div className="flex items-center justify-end gap-2 w-full max850:[&_button]:min-h-[44px] max850:[&_button]:min-w-[44px]">
            <CustomerContactActions
              customer={company}
              variant="icon"
              reminderType="payable"
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export default PayableCompanyCard;
